import { listDuelSpecKeys } from './duelSpecs';
import { DIRECTIONS } from './overtakeCards';
import { SIDES } from './overtakeDeck';
import { LEAD_RULES } from './overtakeMatch';

/**
 * A match as a DOCUMENT, and the bounds it has to stay inside (SPEC-011 PR-11.4).
 *
 * The twin of `leaderboardEntry.js`: pure, and the client-side copy of rules
 * that live somewhere else. The authority is
 * `~/projects/pwa/firestore.rules`, under `match /legends/matches/entries/{matchId}`,
 * because Legends may not deploy rules (CON-003) and the shared project has one
 * ruleset. **A change to the shape here is a change there**, and the two cite
 * each other by path so neither can be edited without finding the other.
 *
 * What this module is NOT is a second rulebook. Whether a move is legal is
 * `overtakeMoves`, and it is the same code on both devices. This is only about
 * whether a document is one the app can act on at all — which is a different
 * question, because a document arrives from a network and from another client's
 * build, and might be from a version that has not shipped yet.
 *
 * THE SIDES ARE ABSOLUTE. `player` and `opponent` name SEATS here, not points of
 * view: `seats.player` is whoever created the match. Each client works out which
 * seat is theirs and the screen is told (`PlayScreen`'s `mySide`). Storing them
 * relative to a viewer would mean one document that means two different things,
 * which is the bug where the guest is shown the host's hand as their own.
 */

/**
 * The shape version, checked on read and written on create.
 *
 * A match is short-lived and there is no migration story, deliberately: a
 * document from an older shape is REFUSED rather than upgraded, and the reader
 * is told the match cannot be opened. Migrating a half-played hand between two
 * clients running different builds is a class of bug nobody should carry for a
 * card game that takes four minutes.
 */
export const MATCH_SCHEMA_VERSION = 1;

/** The statuses a stored match may be in. `unavailable` is never written. */
export const STORED_STATUSES = ['leading', 'renaming', 'answering', 'roundOver', 'over'];

/** Long enough not to be guessed, short enough to live in a link somebody sends. */
export const MATCH_ID_LENGTH = 20;

const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const MATCH_ID = /^[a-z0-9]{20}$/u;
const CAR_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;

/**
 * A new match id.
 *
 * Unguessable is the whole security model of an invite: there is no listing
 * query and no lobby, so a match is reachable only by somebody holding its link
 * (ADR-0006). Twenty characters of this alphabet is about 103 bits, which is not
 * a number anybody enumerates for a card game.
 *
 * `random` is a defaulted parameter, as everywhere else in this tree, so a test
 * can ask what a given draw produces.
 */
export function newMatchId({ random = Math.random } = {}) {
    return Array.from({ length: MATCH_ID_LENGTH }, () => ID_ALPHABET[
        Math.min(Math.floor(random() * ID_ALPHABET.length), ID_ALPHABET.length - 1)
    ]).join('');
}

export function isMatchId(value) {
    return typeof value === 'string' && MATCH_ID.test(value);
}

function isCardList(value, { max }) {
    return Array.isArray(value)
        && value.length <= max
        && value.every((id) => typeof id === 'string' && CAR_ID.test(id));
}

function isSeatMap(value, { of }) {
    return Boolean(value)
        && typeof value === 'object'
        && !Array.isArray(value)
        && SIDES.every((side) => of(value[side]));
}

/**
 * Is this a state the app can act on?
 *
 * Every bound here has a counterpart in the ruleset, and the reason to keep both
 * is the reason SPEC-009 D-4 gives: what the bounds buy is the difference
 * between a plausible document and one with a nine-hundred-card hand in it. The
 * client checks so a rejected write is a bug; the rules check so a client that
 * skipped the check cannot land one.
 */
export function isValidMatchState(state) {
    const figures = new Set(listDuelSpecKeys());

    return Boolean(state)
        && typeof state === 'object'
        && STORED_STATUSES.includes(state.status)
        && isSeatMap(state.hands, { of: (hand) => isCardList(hand, { max: 12 }) })
        && isSeatMap(state.pots, { of: (pot) => isCardList(pot, { max: 60 }) })
        && isSeatMap(state.bonuses, { of: (bonus) => Number.isInteger(bonus) && bonus >= 0 })
        && isCardList(state.stock, { max: 60 })
        && Array.isArray(state.pile)
        && state.pile.length <= 24
        && state.pile.every((played) => typeof played?.id === 'string'
            && CAR_ID.test(played.id)
            && SIDES.includes(played.by))
        && (state.spec === null || figures.has(state.spec))
        && (state.direction === null || DIRECTIONS.includes(state.direction))
        && Array.isArray(state.spent)
        && state.spent.length <= figures.size
        && state.spent.every((spec) => figures.has(spec))
        && SIDES.includes(state.lead)
        && (state.turn === null || SIDES.includes(state.turn))
        && LEAD_RULES.includes(state.leadRule)
        && Number.isInteger(state.round)
        && state.round >= 0
        && state.round <= 60
        && typeof state.doubled === 'boolean'
        && isSeatMap(state.powers, { of: (powers) => Boolean(powers)
            && typeof powers === 'object'
            && ['counter', 'veto', 'double'].every((power) => typeof powers[power] === 'boolean') });
}

/** Is this a document the app can open? */
export function isValidMatchDocument(document) {
    return Boolean(document)
        && document.schemaVersion === MATCH_SCHEMA_VERSION
        && typeof document.seats?.player === 'string'
        && document.seats.player.length > 0
        && (document.seats.opponent === null || (typeof document.seats.opponent === 'string'
            && document.seats.opponent.length > 0
            && document.seats.opponent !== document.seats.player))
        && typeof document.names?.player === 'string'
        && document.names.player.length <= 60
        && (document.names.opponent === null || (typeof document.names.opponent === 'string'
            && document.names.opponent.length <= 60))
        && isValidMatchState(document.state);
}

/**
 * Which seat is this account's, or null for a spectator.
 *
 * Null is a real answer rather than an error: somebody who is handed a link to a
 * match that already has two players is not doing anything wrong, and the screen
 * tells them the seat is taken.
 */
export function seatFor(document, uid) {
    if (!uid || !document?.seats) {
        return null;
    }

    return SIDES.find((side) => document.seats[side] === uid) ?? null;
}

/** Is there still a seat to take? */
export function isOpenForJoining(document, uid) {
    return Boolean(document)
        && document.seats?.opponent === null
        && document.seats?.player !== uid;
}

/**
 * The document a freshly dealt match is created as.
 *
 * The state goes in WHOLE, because SPEC-011 D-1 made it card ids from the first
 * commit — there is no projection step, no second shape, and nothing here that
 * has to be kept in step with the reducer. `overtakeInvite.test.js` pins that by
 * round-tripping a real match rather than a literal.
 */
export function buildMatchDocument({ state, host, name }) {
    return {
        schemaVersion: MATCH_SCHEMA_VERSION,
        seats: { player: host, opponent: null },
        names: { player: String(name ?? '').slice(0, 60), opponent: null },
        state,
    };
}

/**
 * The document with a second player seated.
 *
 * The state is carried through UNTOUCHED, which is what lets the ruleset check a
 * join by comparing it: a write that seats a player and moves a card at the same
 * time is not a join, and there is no legitimate move that needs to be one.
 */
export function seatOpponent(document, { uid, name }) {
    return {
        ...document,
        seats: { ...document.seats, opponent: uid },
        names: { ...document.names, opponent: String(name ?? '').slice(0, 60) },
    };
}

/**
 * May this account write this state over that one?
 *
 * The client's copy of the ruleset's turn check, and it exists so a move is
 * refused before it is sent rather than after. Two ways to hold the pen:
 *
 * - it is your turn, which is the ordinary case; or
 * - the round is over, where either player may deal the next one. A rule that
 *   made only the passer advance would leave the winner of a pot sitting on a
 *   result waiting for the person who just lost it.
 */
export function canWriteOver(document, uid) {
    const seat = seatFor(document, uid);

    if (!seat || !document?.state) {
        return false;
    }

    return document.state.status === 'roundOver' || document.state.turn === seat;
}
